import { useState, useMemo, useRef, useCallback } from "react";

/**
 * Typed filter + optional pin after a selection (input clears, list can stay filtered).
 * Clears pin and input on Autocomplete close reason "blur" or "escape" (refocus shows full list).
 */
export function usePinnedFilterState() {
  const [filterValue, setFilterValue] = useState("");
  const [pinnedFilter, setPinnedFilter] = useState("");
  const ignoreNextEmptyInputRef = useRef(false);

  const effectiveFilter = useMemo(() => {
    const typed = (filterValue || "").trim();
    if (typed !== "") return filterValue;
    return pinnedFilter || "";
  }, [filterValue, pinnedFilter]);

  const handleInputChange = useCallback((event, newFilterValue, reason) => {
    if (reason === "reset") return;
    if (newFilterValue === "" && ignoreNextEmptyInputRef.current) {
      ignoreNextEmptyInputRef.current = false;
      setFilterValue("");
      return;
    }
    ignoreNextEmptyInputRef.current = false;
    setPinnedFilter("");
    setFilterValue(newFilterValue);
  }, []);

  const createOnCloseHandler = useCallback((onHostClose) => (event, reason) => {
    if (typeof onHostClose === "function") {
      onHostClose(event, reason);
    }
    if (reason === "blur" || reason === "escape") {
      setPinnedFilter("");
      setFilterValue("");
    }
  }, []);

  /** After picking a real option: pin typed text if any; always clear input (keeps list filtered on pin). */
  const afterListItemSelection = useCallback(() => {
    if ((filterValue || "").trim() !== "") {
      setPinnedFilter(filterValue);
    }
    ignoreNextEmptyInputRef.current = true;
    setFilterValue("");
  }, [filterValue]);

  /** After Select All: clear filter + pin. */
  const afterSelectAllSelection = useCallback(() => {
    setPinnedFilter("");
    setFilterValue("");
  }, []);

  return {
    filterValue,
    pinnedFilter,
    effectiveFilter,
    handleInputChange,
    createOnCloseHandler,
    afterListItemSelection,
    afterSelectAllSelection,
  };
}
